import { mergeClasses } from "@fluentui/react-components";
import { useStyles } from "./currentQuestionShelf.styles";
import { ICurrentQuestionShelfProps } from "./currentQuestionShelf.types";
import {
  OBJECTIVE_TYPE_LIKERT_SCALE,
  OBJECTIVE_TYPE_MULTIPLE_CHOICE,
} from "apps/diagnostics/models/IObjective";

export const CurrentQuestionShelf = ({
  question,
  objectiveName,
  accentPalette,
  transitionMessage,
  introduction,
  statementNumber,
  totalStatements,
  objectiveType,
  language,
}: ICurrentQuestionShelfProps): JSX.Element => {
  const styles = useStyles();
  const bubbleStyle = accentPalette
    ? { borderLeftColor: accentPalette.accent }
    : undefined;

  const isSelectOptions =
    objectiveType === OBJECTIVE_TYPE_MULTIPLE_CHOICE ||
    objectiveType === OBJECTIVE_TYPE_LIKERT_SCALE;
  const segmentIndexes = Array.from(
    { length: isSelectOptions ? (totalStatements as number) : 0 },
    (_, i) => i + 1,
  );

  const getSelectOptionsDescription = (): string => {
    switch (objectiveType) {
      case OBJECTIVE_TYPE_MULTIPLE_CHOICE:
        return language.multipleChoiceDescription;
      case OBJECTIVE_TYPE_LIKERT_SCALE:
        return language.ratingSectionDescription;
      default:
        return "";
    }
  };

  const getProgressCounterLabel = (): string => {
    switch (objectiveType) {
      case OBJECTIVE_TYPE_MULTIPLE_CHOICE:
        return language.questionCounter;
      case OBJECTIVE_TYPE_LIKERT_SCALE:
        return language.statementCounter;
      default:
        return "";
    }
  };

  return (
    <div className={styles.shelf}>
      <div className={styles.inner} aria-live="polite">
        {transitionMessage && (
          <p className={styles.transitionMessage}>{transitionMessage}</p>
        )}
        {isSelectOptions ? (
          <div
            className={styles.currentQuestionBubble}
            style={bubbleStyle}
            role="region"
            aria-label="Current question"
          >
            <div className={styles.optionsIntro}>
              {objectiveName && (
                <span className={styles.objectiveLabel}>{objectiveName}</span>
              )}
              <span className={styles.optionSelectDescription}>
                {introduction && (
                  <span className={styles.introduction}>{introduction}</span>
                )}
                {getSelectOptionsDescription()}
              </span>
            </div>
            <div className={styles.progressCounterRow}>
              <span
                className={styles.progressCounterText}
                style={
                  accentPalette ? { color: accentPalette.accent } : undefined
                }
              >
                {getProgressCounterLabel()
                  .replace("{current}", String(statementNumber))
                  .replace("{total}", String(totalStatements))}
              </span>
              <div
                className={styles.progressTrack}
                role="progressbar"
                aria-valuenow={statementNumber}
                aria-valuemin={1}
                aria-valuemax={totalStatements}
              >
                {segmentIndexes.map((segmentNumber) => (
                  <div
                    key={segmentNumber}
                    className={mergeClasses(
                      styles.progressSegment,
                      segmentNumber <= (statementNumber ?? 0) &&
                      styles.progressSegmentActive,
                    )}
                    style={
                      segmentNumber <= (statementNumber ?? 0) && accentPalette
                        ? { backgroundColor: accentPalette.accent }
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
            <span>{question}</span>
          </div>
        ) : (
          <div
            className={styles.currentQuestionBubble}
            style={bubbleStyle}
            role="region"
            aria-label="Current question"
          >
            {objectiveName && (
              <span className={styles.objectiveLabel}>{objectiveName}</span>
            )}
            {question}
          </div>
        )}
      </div>
    </div>
  );
};
